// deliveryOutcome.js
// -----------------------------------------------------------------------------
// The two ways a visit can end other than "delivered, nothing more to do", as
// pure rules: which reasons a driver may pick, when a report may be saved, and
// what it writes.
//
//   ส่งไม่สำเร็จ (rejected)   The customer did not accept the products. The
//                             stop closes as `failed` (driverDay.REJECTED);
//                             delivery staff rebook it by hand.
//   ต้องติดตามงาน (follow-up)  The customer kept the products but needs more
//                             support later. The stop is delivered as usual,
//                             and carries a flag the monitor board shows.
//
// Reasons are several-at-once: a cabinet can be both the wrong colour and
// missing parts. อื่นๆ is allowed on both lists and then needs a note, because a
// bare "other" tells whoever reads it nothing.
//
// Pure: no Vue, no Firestore, CommonJS, so scripts/check-driver-day.js can
// require() it.
// -----------------------------------------------------------------------------

const P = require('./proofPhoto')

const OTHER = 'อื่นๆ'

const REJECT_REASONS = [
  'สินค้ามีตำหนิ', 'สีผิด', 'ลูกค้าไม่อยู่', 'อุปกรณ์ไม่ครบ',
  'อุปกรณ์ผิดรุ่น', 'ประกอบไม่ได้', 'สถานที่จัดส่งผิด', OTHER
]

const FOLLOW_UP_REASONS = ['ตู้มีตำหนิ', 'สีผิด', 'อุปกรณ์ไม่ครบ', 'อุปกรณ์ผิดรุ่น', OTHER]

// The reasons as picked, kept only where the list knows them, de-duplicated,
// and in the list's own order -- so the same report reads the same way on the
// board whatever order the driver tapped the chips in.
function cleanReasons (picked, allowed) {
  const given = Array.isArray(picked) ? picked : []
  return allowed.filter(r => given.indexOf(r) > -1)
}

function reasonsProblem (picked, allowed, note) {
  const reasons = cleanReasons(picked, allowed)
  if (!reasons.length) return 'เลือกเหตุผลอย่างน้อย 1 ข้อ'
  if (reasons.indexOf(OTHER) > -1 && !String(note || '').trim()) {
    return 'เลือก "' + OTHER + '" แล้ว กรุณาพิมพ์รายละเอียด'
  }
  return ''
}

// Why the ส่งไม่สำเร็จ sheet may not be saved yet, or ''. Reasons first, then the
// evidence, in the order the sheet asks for them.
function rejectProblem (input) {
  const p = input || {}
  return reasonsProblem(p.reasons, REJECT_REASONS, p.note) ||
    P.photoProblem('rejected', p.photoCount, p.photoSkippedReason)
}

// What the ส่งไม่สำเร็จ sheet writes onto deliveryStop.proof.
function rejectFields (input) {
  const p = input || {}
  return Object.assign({
    rejectReasons: cleanReasons(p.reasons, REJECT_REASONS),
    rejectNote: String(p.note || '').trim()
  }, P.photoFields('rejected', p.photoUrls, p.photoSkippedReason))
}

// Why the follow-up part of the driver's closing page may not be saved, or ''.
// Nothing to check when the driver did not flag the job.
function followUpProblem (input) {
  const p = input || {}
  if (!p.followUp) return ''
  return reasonsProblem(p.reasons, FOLLOW_UP_REASONS, p.note)
}

// What the closing page writes about follow-up. Always all three fields, so a
// flag un-ticked on a retry clears the reasons it had.
function followUpFields (input) {
  const p = input || {}
  const on = !!p.followUp
  return {
    followUp: on,
    followUpReasons: on ? cleanReasons(p.reasons, FOLLOW_UP_REASONS) : [],
    followUpNote: on ? String(p.note || '').trim() : ''
  }
}

// The fields mirrored onto each deliveryList row with the closing stamp, so a
// staff screen reading deliveryList can tell the outcome without a join. Only a
// rejection and a flagged follow-up write anything: an ordinary delivery
// changes no field it did not change before this existed.
function listMirror (step, proof) {
  const p = proof || {}
  if (step === 'rejected') {
    return {
      deliveryResult: 'rejected',
      rejectReasons: p.rejectReasons || [],
      rejectNote: p.rejectNote || ''
    }
  }
  if (step === 'finished' && p.followUp) {
    return {
      followUp: true,
      followUpReasons: p.followUpReasons || [],
      followUpNote: p.followUpNote || ''
    }
  }
  return {}
}

// One line for a person to read: "สีผิด, อื่นๆ — ขาตู้หัก".
function reasonText (reasons, note) {
  const list = Array.isArray(reasons) ? reasons.join(', ') : ''
  const n = String(note || '').trim()
  if (list && n) return list + ' — ' + n
  return list || n
}

module.exports = {
  OTHER,
  REJECT_REASONS,
  FOLLOW_UP_REASONS,
  cleanReasons,
  rejectProblem,
  rejectFields,
  followUpProblem,
  followUpFields,
  listMirror,
  reasonText
}
